import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../parts/input-label/input-label.component';
import { AmountInput } from '../../parts/amount-input/amount-input.component';
import { ChartOfAccountCategory } from '../../core/enums/data.enums';
import { CustomValidators } from '../../core/validators/custom-validators';
import { UserSessionService } from '../../core/services/user-session.service';
import { FinancialService } from '../../core/services/financial/financial.service';
import { CreateExpenseRequest } from '../../core/models/data/expense.model';
import { ConfirmationModalComponent } from '../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../core/enums/base.enums';
import { BootstrapService } from '../../core/services/bootstrap.service';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../core/constants/messaging.constants';
import { CurrencyUtilities } from '../../core/utilities/currency.utilities';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../core/services/dirty-form-registry.service';
import { ChartOfAccountTypeaheadComponent } from '../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';
import { PageTitleComponent } from '../../parts/page-title/page-title.component';
import { DatePlaceholderDirective } from '../../core/directives/date-placeholder.directive';

@Component({
  selector: 'ms-expense-form',
  standalone: true,
  imports: [
    PageTitleComponent,
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    InputLabel,
    AmountInput,
    ConfirmationModalComponent,
    InfoModalComponent,
    ChartOfAccountTypeaheadComponent,
    DatePlaceholderDirective
],
  templateUrl: './expense.form.html',
})
export class ExpenseForm extends BaseFormComponent implements CanComponentDeactivate {

  //#region Properties & Signals
  public expenseForm: FormGroup;
  public ChartOfAccountCategory = ChartOfAccountCategory;

  @ViewChild('expenseConfirmationModalRef') expenseConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('expenseInfoModalRef') expenseInfoModalRef!: InfoModalComponent;

  //#endregion Properties & Signals

  //#region Constructor & Initialization

  private dirtyFormRegistry = inject(DirtyFormRegistryService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private financialService: FinancialService,
    public userSessionService: UserSessionService,
    private bootstrapService: BootstrapService,
    private routerService: Router
  ) {
    super();

    this.dirtyFormRegistry.register(this);
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

    this.expenseForm = this.fb.group({
      vendorName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(250)]],
      paidDate: ['', [Validators.required, CustomValidators.noFutureDate('Paid date cannot be in the future')]],
      amount: ['', [Validators.required, Validators.min(0.01)]],
      referenceNumber: ['', [Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9\-\/]*$/)]],
      description: ['', [Validators.maxLength(500)]],
      expenseAccount: [null, [Validators.required, Validators.min(1)]],
      liabilityAccount: [null, [Validators.required, Validators.min(1)]],
    });
  }

  //#endregion Constructor & Initialization

  //#region Form Submission

  public async onSubmit(): Promise<void> {
    if (this.expenseForm.invalid) {
      this.expenseForm.markAllAsTouched();
      return;
    }

    const orgId = this.userSessionService.activeOrganization()?.organizationId;
    if (!orgId) {
      return;
    }

    this.isSubmitting = true;
    try {
      const request = this.buildSubmissionRequest(orgId);
      const result = await this.financialService.createExpense(request);

      if (result.success) {
        this.submitSucceeded = true;
        this.expenseConfirmationModalRef.modalType.set(ModalTypes.Success);
        this.expenseConfirmationModalRef.message.set(
          result.message || BalancesMessagingConstants.EXPENSE_CREATE_SUCCESS()
        );
        this.expenseConfirmationModalRef.cancelButtonText.set('Leave');
        this.expenseConfirmationModalRef.confirmButtonText.set('Create Another');
        this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Show);
      } else {
        this.expenseInfoModalRef.modalType.set(ModalTypes.Failure);
        this.expenseInfoModalRef.message.set(
          result.message || BalancesMessagingConstants.EXPENSE_CREATE_FAILURE()
        );
        this.bootstrapService.manageModal('#expenseInfoModal', ModalActions.Show);
      }
    } catch (error) {
      console.error('Exception caught:', error);
      this.expenseInfoModalRef.modalType.set(ModalTypes.Failure);
      this.expenseInfoModalRef.message.set(BalancesMessagingConstants.EXPENSE_CREATE_FAILURE());
      this.bootstrapService.manageModal('#expenseInfoModal', ModalActions.Show);
    } finally {
      this.isSubmitting = false;
    }
  }

  private buildSubmissionRequest(organizationId: number): CreateExpenseRequest {
    const formValue = this.expenseForm.value;

    const amountString = String(formValue.amount || '0');
    const amount = CurrencyUtilities.convertCurrencyStringToMinorUnits(amountString, 100);

    return {
      organizationId,
      vendorName: formValue.vendorName?.trim(),
      datePaid: formValue.paidDate,
      amount,
      referenceNumber: formValue.referenceNumber || null,
      description: formValue.description?.trim() || null,
      expenseAccount: formValue.expenseAccount,
      liabilityAccount: formValue.liabilityAccount,
    };
  }

  //#endregion Form Submission

  //#region UI Helper Methods

  public onCancel(): void {
    if (this.expenseForm.dirty) {
      this.expenseConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.expenseConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Show);
    } else {
      this.routerService.navigate(['/expenses']);
    }
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.expenseForm.dirty && !this.submitSucceeded, () => {
      this.expenseConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.expenseConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Show);
    });
  }

  public resetForm(): void {
    this.submitSucceeded = false;
    this.expenseForm.reset({
      paidDate: '',
      expenseAccount: null,
      liabilityAccount: null,
    });
  }

  //#endregion UI Helper Methods

  //#region Modal Management

  public confirmationConfirmFunction(): void {
    if (this.resolvePendingLeave(true)) {
      this.resetForm();
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Hide);
      return;
    }

    this.resetForm();

    if (this.expenseConfirmationModalRef.modalType() === ModalTypes.Success) {
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Hide);
    } else {
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Hide);
      this.routerService.navigate(['/expenses']);
    }
  }

  public confirmationCancelFunction(): void {
    if (this.resolvePendingLeave(false)) {
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Hide);
      return;
    }

    if (this.expenseConfirmationModalRef.modalType() === ModalTypes.Success) {
      this.resetForm();
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Hide);
      this.routerService.navigate(['/expenses']);
    } else {
      this.bootstrapService.manageModal('#expenseConfirmationModal', ModalActions.Hide);
    }
  }

  public infoModalConfirmed(): void {
    this.bootstrapService.manageModal('#expenseInfoModal', ModalActions.Hide);
  }

  //#endregion Modal Management

}
